import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, InjectionToken, makeEnvironmentProviders } from '@angular/core';
import {
    IdpOauth2AuthorizeAction,
    IdpOauth2GrantType,
    IdpOauth2LoginCodeChallengeMethod,
    IdpOauth2LoginResponseType,
    Oauth2AuthorizeQueryDto,
    Oauth2CreateTokenDto,
    Oauth2CreateTokenResultDto,
    Oauth2RevokeTokenDto,
    SignInResultDto,
} from '@icp/client-api';
import { catchError, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { rxjsRetryWithBackoff } from '../../shared/utils';
import { Oauth2TokenDto } from '../interfaces/oauth';
import { getCodeChallenge, randomString } from '../util/utils';

export interface AuthSession {
    accessToken: string;
    refreshToken: string;
    clientId: string;
}

export interface AccessToken {
    aud: string;
    azp: string;
    exp: number;
    iat: number;
    iss: string;
    scope: string;
    sub: string;
    token_use: string;
}

export interface DecodedAuthSession extends AuthSession {
    decodedAccessToken: AccessToken;
}

export interface IdpServiceConfig {
    idpUrl: string;
    clientId: string | null;
}

interface AuthorizeParams {
    state: string;
    nonce: string;
    redirect_uri: string;
}

const IDP_SERVICE_CONFIG = new InjectionToken<IdpServiceConfig>('idp-service-config');

export function provideIdpServiceConfig(config: IdpServiceConfig) {
    return makeEnvironmentProviders([{ provide: IDP_SERVICE_CONFIG, useValue: config }]);
}

@Injectable({ providedIn: 'root' })
export class IdpService {
    private config = inject(IDP_SERVICE_CONFIG);
    private http = inject(HttpClient);
    private isRefreshingAccessToken = false;
    private refreshTokenObservable: Observable<AuthSession> | null = null;

    /**
     * Validates if the auth token is still valid.
     * Refreshes the token if it has expired using the refresh token.
     */
    getCurrentSession(): Observable<AuthSession | null> {
        const session = this.getSessionFromStorage();
        if (!session) {
            return of(null);
        }
        const now = new Date();
        // Refresh 1 minute too early
        if (session.decodedAccessToken.exp * 1000 < now.getTime() + 60000) {
            return this.refreshToken({
                client_id: this.config.clientId!,
                grant_type: IdpOauth2GrantType.REFRESH_TOKEN,
                refresh_token: session.refreshToken,
            }).pipe(
                catchError((err) => {
                    if (err instanceof HttpErrorResponse) {
                        if (err.status === HttpStatusCode.GatewayTimeout || err.status === 0) {
                            // trigger retry
                            return throwError(() => err);
                        }
                    }
                    // if an error is still throw, let's just clear all
                    // Could happen when our refresh token is revoked server side
                    this.deleteSessionFromStorage();
                    return of(null);
                }),
                rxjsRetryWithBackoff(10, 500),
            );
        }
        return of(session);
    }

    authorize(
        payload: {
            scope: string;
            redirectUri: string;
            action?: IdpOauth2AuthorizeAction;
            login_hint?: string;
        },
        extraState?: any,
    ) {
        const nonce = randomString(36);
        const state = randomString(36);
        const codeVerifier = randomString(32);
        const challenge = getCodeChallenge(codeVerifier);
        localStorage.setItem(`${this.getStoragePrefix()}.codeVerifier`, codeVerifier);
        localStorage.setItem(`${this.getStoragePrefix()}.extraState`, JSON.stringify(extraState) ?? '');

        const params: Oauth2AuthorizeQueryDto = {
            response_type: IdpOauth2LoginResponseType.CODE,
            code_challenge: challenge,
            code_challenge_method: IdpOauth2LoginCodeChallengeMethod.S256,
            client_id: this.config.clientId!,
            redirect_uri: payload.redirectUri,
            nonce,
            state,
            scope: payload.scope,
        };
        if (payload.action) {
            params.action = payload.action;
        }
        if (payload.login_hint) {
            params.login_hint = payload.login_hint;
        }
        const httpParams = new HttpParams({
            fromObject: params as unknown as { [param: string]: string },
        });
        const authorizeParams: AuthorizeParams = {
            state,
            nonce,
            redirect_uri: payload.redirectUri,
        };
        localStorage.setItem(`${this.getStoragePrefix()}.authorizeParams`, JSON.stringify(authorizeParams));
        return `${this.config.idpUrl}/idp/oauth2/v1/authorize?${httpParams}`;
    }

    refreshToken(data: Oauth2CreateTokenDto): Observable<AuthSession> {
        if (this.isRefreshingAccessToken) {
            return this.refreshTokenObservable!;
        }
        this.isRefreshingAccessToken = true;
        this.refreshTokenObservable = this.http
            .post<Oauth2CreateTokenResultDto>(`${this.config.idpUrl}/idp/oauth2/v1/token`, data)
            .pipe(
                tap(() => (this.isRefreshingAccessToken = false)),
                map((result) => this.storeSession(result)),
                shareReplay(1),
            );
        return this.refreshTokenObservable;
    }

    logout() {
        const session = this.getSessionFromStorage();
        if (session) {
            return this.revokeToken({ client_id: session.clientId, token: session.refreshToken });
        } else {
            this.deleteSessionFromStorage();
            return of(null);
        }
    }

    revokeToken(data: Oauth2RevokeTokenDto) {
        return this.http.post<void>(`${this.config.idpUrl}/idp/oauth2/v1/revoke`, data).pipe(
            catchError(() => {
                return of(null);
            }),
            tap(() => this.deleteSessionFromStorage()),
        );
    }

    storeSession(data: { accessToken: string; refreshToken: string }): DecodedAuthSession {
        localStorage.setItem(`${this.getStoragePrefix()}.session`, JSON.stringify(data));
        return {
            clientId: this.config.clientId!,
            refreshToken: data.refreshToken,
            accessToken: data.accessToken,
            decodedAccessToken: this.decodeAccessTokenJwt(data.accessToken)!,
        };
    }

    /**
     * Returns any extra state passed in the authorize method
     * @param state
     * @param code
     */
    createIcpToken(state: string, code: string): Observable<{ extraState?: any; token: Oauth2TokenDto }> {
        const codeVerifier = localStorage.getItem(`${this.getStoragePrefix()}.codeVerifier`) as string;
        const params: AuthorizeParams = JSON.parse(localStorage.getItem(`${this.getStoragePrefix()}.authorizeParams`)!);
        if (state !== params.state || !params.redirect_uri) {
            throw new Error('State does not match');
        }

        const body: Oauth2CreateTokenDto = {
            grant_type: IdpOauth2GrantType.AUTHORIZATION_CODE,
            code,
            redirect_uri: params.redirect_uri,
            client_id: this.config.clientId!,
            code_verifier: codeVerifier,
        };

        return this.http.post<SignInResultDto>(`${this.config.idpUrl}/idp/oauth2/v1/token`, body).pipe(
            map(({ refreshToken, accessToken, idToken }) => {
                const parsedToken = JSON.parse(atob(idToken.split('.')[1]));
                if (parsedToken.nonce !== params.nonce) {
                    throw new Error('nonce does not match');
                }
                this.storeSession({ refreshToken, accessToken });
                const extraState = JSON.parse(localStorage.getItem(`${this.getStoragePrefix()}.extraState`) || '{}');
                return {
                    ...extraState,
                    token: parsedToken,
                };
            }),
        );
    }

    deleteSessionFromStorage() {
        localStorage.removeItem(`${this.getStoragePrefix()}.session`);
    }

    private getSessionFromStorage(): DecodedAuthSession | null {
        const tokens = localStorage.getItem(`${this.getStoragePrefix()}.session`);
        const parsed = this.tryParse(tokens);
        return parsed
            ? {
                  ...parsed,
                  clientId: this.config.clientId,
                  decodedAccessToken: this.decodeAccessTokenJwt(parsed.accessToken),
              }
            : parsed;
    }

    private getStoragePrefix() {
        return `icpIdp.${this.config.clientId}`;
    }

    private decodeAccessTokenJwt(accessToken: string): AccessToken {
        return JSON.parse(atob(accessToken.split('.')[1]).toString());
    }

    private tryParse(data: string | null) {
        if (data === null) {
            return data;
        }
        try {
            return JSON.parse(data);
        } catch {
            return null;
        }
    }
}
